import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const generateMultipleSilosExcel = async (dataBySilo, locationInfo) => {
  if (!dataBySilo || Object.keys(dataBySilo).length === 0) {
    console.error('No hay datos para exportar');
    return;
  }

  // Obtener el rango de fechas de todos los silos
  const allDates = Object.values(dataBySilo).flat().map(row => new Date(row.datetime_fetched));
  const startDate = new Date(Math.min(...allDates)).toLocaleDateString();
  const endDate = new Date(Math.max(...allDates)).toLocaleDateString();
  
  // Crear nombre del archivo con la información de ubicación
  const siloCount = Object.keys(dataBySilo).length;
  const fileName = `Dados de Silo - ${locationInfo.farmName} - ${locationInfo.barnName} - ${siloCount} Silo(s) - ${startDate} - ${endDate}.xlsx`;

  const workbook = new ExcelJS.Workbook();

  // Crear una hoja por cada silo
  Object.entries(dataBySilo).forEach(([siloName, data]) => {
    const worksheet = workbook.addWorksheet(siloName);
    
    // Configurar las columnas
    const columns = [
      { key: 'silo_name', width: 15 },
      { key: 'datetime_fetched', width: 20 },
      { key: 'weight', width: 15 },
      { key: 'level', width: 15 },
      { key: 'volume', width: 15 },
      { key: 'food_name', width: 20 },
      { key: 'food_density', width: 18 }
    ];

    worksheet.columns = columns;

    // Agregar título y rango de fechas
    const titleCell = worksheet.getCell('B1');
    titleCell.value = `Dados de ${siloName}`;
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: 'center' };

    // Agregar información de ubicación
    const locationCell = worksheet.getCell('B2');
    locationCell.value = `${locationInfo.farmName} - ${locationInfo.barnName}`;
    locationCell.font = { bold: true };
    locationCell.alignment = { horizontal: 'center' };

    const dateRangeCell = worksheet.getCell('B3');
    dateRangeCell.value = `${startDate} - ${endDate}`;
    dateRangeCell.font = { italic: true };
    dateRangeCell.alignment = { horizontal: 'center' };

    // Ajustar ancho de la columna B
    worksheet.getColumn('B').width = 40;

    // Establecer los headers en la fila 5
    const headerRow = worksheet.getRow(5);
    headerRow.values = ['Silo', 'Data/Hora', 'Peso (kg)', 'Nível (%)', 'Volume (m³)', 'Matéria Prima', 'Densidade (kg/m³)'];
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFEEEEEE' }
    };

    // Agregar datos
    data.forEach((row, index) => {
      const rowData = {
        silo_name: row.silo_name || `Silo ${row.silo || 'N/A'}`,
        datetime_fetched: row.datetime_fetched ? format(new Date(row.datetime_fetched), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A',
        weight: row.weight != null ? Number(row.weight.toFixed(2)) : 0,
        level: row.level != null ? Number(row.level.toFixed(2)) : 0,
        volume: row.volume != null ? Number(row.volume.toFixed(2)) : 0,
        food_name: row.food_name || 'N/A',
        food_density: row.food_density != null ? Number(row.food_density.toFixed(2)) : 'N/A'
      };

      const currentRow = worksheet.getRow(6 + index);
      currentRow.values = [
        rowData.silo_name,
        rowData.datetime_fetched,
        rowData.weight,
        rowData.level,
        rowData.volume,
        rowData.food_name,
        rowData.food_density
      ];
    });

    // Alinear columnas
    worksheet.getColumn('silo_name').alignment = { horizontal: 'center' };
    worksheet.getColumn('datetime_fetched').alignment = { horizontal: 'center' };
    worksheet.getColumn('weight').alignment = { horizontal: 'center' };
    worksheet.getColumn('level').alignment = { horizontal: 'center' };
    worksheet.getColumn('volume').alignment = { horizontal: 'center' };
    worksheet.getColumn('food_name').alignment = { horizontal: 'center' };
    worksheet.getColumn('food_density').alignment = { horizontal: 'center' };

    // Alternar colores de fila
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 5) { // Skip header and empty rows
        const fill = rowNumber % 2 === 0 
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
        row.eachCell(cell => {
          cell.fill = fill;
        });
      }
    });
  });

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
};

// Función original para un solo silo (mantener compatibilidad)
export const generateSiloDataExcel = async (data, locationInfo) => {
  if (!data || data.length === 0) {
    console.error('No hay datos para exportar');
    return;
  }

  // Obtener el rango de fechas
  const dates = data.map(row => new Date(row.datetime_fetched));
  const startDate = new Date(Math.min(...dates)).toLocaleDateString();
  const endDate = new Date(Math.max(...dates)).toLocaleDateString();
  
  // Crear nombre del archivo con la información de ubicación
  const fileName = `Dados de Silo - ${locationInfo.farmName} - ${locationInfo.barnName} - Silo ${locationInfo.siloName} - ${startDate} - ${endDate}.xlsx`;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Dados de Silo');

  // Configurar las columnas
  const columns = [
    { key: 'silo_name', width: 15 },
    { key: 'datetime_fetched', width: 20 },
    { key: 'weight', width: 15 },
    { key: 'level', width: 15 },
    { key: 'volume', width: 15 },
    { key: 'food_name', width: 20 },
    { key: 'food_density', width: 18 }
  ];

  worksheet.columns = columns;

  // Agregar título y rango de fechas
  const titleCell = worksheet.getCell('B1');
  titleCell.value = 'Dados de Silo';
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: 'center' };

  // Agregar información de ubicación
  const locationCell = worksheet.getCell('B2');
  locationCell.value = `${locationInfo.farmName} - ${locationInfo.barnName} - Silo ${locationInfo.siloName}`;
  locationCell.font = { bold: true };
  locationCell.alignment = { horizontal: 'center' };

  const dateRangeCell = worksheet.getCell('B3');
  dateRangeCell.value = `${startDate} - ${endDate}`;
  dateRangeCell.font = { italic: true };
  dateRangeCell.alignment = { horizontal: 'center' };

  // Ajustar ancho de la columna B
  worksheet.getColumn('B').width = 40;

  // Establecer los headers en la fila 5
  const headerRow = worksheet.getRow(5);
  headerRow.values = ['Silo', 'Data/Hora', 'Peso (kg)', 'Nível (%)', 'Volume (m³)', 'Matéria Prima', 'Densidade (kg/m³)'];
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFEEEEEE' }
  };

  // Agregar datos
  data.forEach((row, index) => {
    const rowData = {
      silo_name: row.silo_name || `Silo ${row.silo || 'N/A'}`,
      datetime_fetched: row.datetime_fetched ? format(new Date(row.datetime_fetched), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A',
      weight: row.weight != null ? Number(row.weight.toFixed(2)) : 0,
      level: row.level != null ? Number(row.level.toFixed(2)) : 0,
      volume: row.volume != null ? Number(row.volume.toFixed(2)) : 0,
      food_name: row.food_name || 'N/A',
      food_density: row.food_density != null ? Number(row.food_density.toFixed(2)) : 'N/A'
    };

    const currentRow = worksheet.getRow(6 + index);
    currentRow.values = [
      rowData.silo_name,
      rowData.datetime_fetched,
      rowData.weight,
      rowData.level,
      rowData.volume,
      rowData.food_name,
      rowData.food_density
    ];
  });

  // Alinear columnas
  worksheet.getColumn('silo_name').alignment = { horizontal: 'center' };
  worksheet.getColumn('datetime_fetched').alignment = { horizontal: 'center' };
  worksheet.getColumn('weight').alignment = { horizontal: 'center' };
  worksheet.getColumn('level').alignment = { horizontal: 'center' };
  worksheet.getColumn('volume').alignment = { horizontal: 'center' };
  worksheet.getColumn('food_name').alignment = { horizontal: 'center' };
  worksheet.getColumn('food_density').alignment = { horizontal: 'center' };

  // Alternar colores de fila
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 5) { // Skip header and empty rows
      const fill = rowNumber % 2 === 0 
        ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
        : { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      row.eachCell(cell => {
        cell.fill = fill;
      });
    }
  });

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
};

// Función para generar datos fake de múltiples silos
export const generateFakeMultipleSilosData = () => {
  const silos = ['Silo 13', 'Silo 14', 'Silo 15', 'Silo 16'];
  const dataBySilo = {};
  
  silos.forEach(siloName => {
    const fakeData = [];
    const baseDate = new Date('2025-10-20T07:00:00');
    const siloNumber = parseInt(siloName.split(' ')[1]);
    
    for (let i = 0; i < 40; i++) {
      const date = new Date(baseDate.getTime() + (i * 2 * 60 * 60 * 1000)); // Cada 2 horas
      
      // Variar los datos por silo para hacer más realista
      const baseWeight = 7000 + (siloNumber * 200); // Diferentes pesos base por silo
      const baseLevel = 60 + (siloNumber * 5); // Diferentes niveles base por silo
      const baseVolume = 10 + (siloNumber * 0.5); // Diferentes volúmenes base por silo
      
      fakeData.push({
        id: 12586946 + i + (siloNumber * 1000),
        silo: 735 + siloNumber - 13,
        acquisition: 13076393 + i + (siloNumber * 1000),
        silo_feed_weight: 9346323 + (i * 100) + (siloNumber * 1000),
        sensor: 1301 + siloNumber - 13,
        datetime_fetched: date.toISOString(),
        weight: Number((baseWeight + (Math.random() * 800)).toFixed(2)), // Variación alrededor del peso base
        level: Number((baseLevel + (Math.random() * 15)).toFixed(2)), // Variación alrededor del nivel base
        volume: Number((baseVolume + (Math.random() * 1.5)).toFixed(2)), // Variación alrededor del volumen base
        food: 135,
        valid: Math.random() > 0.1, // 90% válidos
        silo_name: siloName
      });
    }
    
    dataBySilo[siloName] = fakeData;
  });
  
  return dataBySilo;
};

// Función para generar datos fake de un solo silo (mantener compatibilidad)
export const generateFakeSiloData = () => {
  const fakeData = [];
  const baseDate = new Date('2025-10-20T07:00:00');
  
  for (let i = 0; i < 50; i++) {
    const date = new Date(baseDate.getTime() + (i * 2 * 60 * 60 * 1000)); // Cada 2 horas
    
    fakeData.push({
      id: 12586946 + i,
      silo: 735,
      acquisition: 13076393 + i,
      silo_feed_weight: 9346323 + (i * 100),
      sensor: 1301,
      datetime_fetched: date.toISOString(),
      weight: Number((7000 + (Math.random() * 1000)).toFixed(2)), // Entre 7000-8000 kg
      level: Number((60 + (Math.random() * 20)).toFixed(2)), // Entre 60-80%
      volume: Number((10 + (Math.random() * 2)).toFixed(2)), // Entre 10-12 m³
      food: 135,
      valid: Math.random() > 0.1, // 90% válidos
      silo_name: "Silo 13"
    });
  }
  
  return fakeData;
};
